"use client";
import { IMAGES } from "@/lib/constants";
import { useGeolocation } from "@/lib/hooks/common.hooks";
import { Typography, useMediaQuery, useTheme } from "@mui/material";
import { useJsApiLoader } from "@react-google-maps/api";
import Image from "next/image";
import { useEffect, useRef } from "react";

const libraries = ["places"];

const MapsComponent = ({ sx,latitude, longitude, setLatitude, setLongitude, onDragStart }) => {
  const mapRef = useRef(null);
  const theme = useTheme();
  const mapInstance = useRef(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
    libraries,
    language: "en",
    region: "us",
    componentRestrictions: { country: "in" },
  });

  const { coordinates } = useGeolocation();
 
  // useEffect(() => {
  //   onLoadStatusChange?.(isLoaded);
  // }, [isLoaded, onLoadStatusChange]);

  useEffect(() => {
    if(!latitude && !longitude && coordinates?.lat && coordinates?.lng){
      setLatitude(coordinates.lat);
      setLongitude(coordinates.lng);
    }
  }, [coordinates]);

  // Initialize map and marker
  useEffect(() => {
    if (isLoaded && mapRef.current && latitude && longitude) {
      const position = {
        lat: parseFloat(latitude),
        lng: parseFloat(longitude),
      };
      if (!mapInstance.current) {
        mapInstance.current = new window.google.maps.Map(mapRef.current, {
          center: position,
          zoom: 17,
          mapTypeId: window.google.maps.MapTypeId.ROADMAP,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          rotateControl: false,
          tilt: 0,
        });
        mapInstance.current.addListener("idle", () => {
          const center = mapInstance.current.getCenter();
          if (center) {
            setLatitude(center.lat());
            setLongitude(center.lng());
          }
        });
        // Add dragstart listener
        mapInstance.current.addListener("dragstart", () => {
          onDragStart?.();
        });
      } else {
        mapInstance.current.setCenter(position);
      }
    }
  }, [isLoaded, latitude, longitude, setLatitude, setLongitude, onDragStart]);

  // Ensure marker and map stay in sync with props even after initial mount
  useEffect(() => {
    if (isLoaded && mapInstance.current && latitude && longitude) {
      const newPos = { lat: parseFloat(latitude), lng: parseFloat(longitude) };
      mapInstance.current.setCenter(newPos);
    }
  }, [latitude, longitude, isLoaded]);

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  return (
    <div style={{ position: "relative" }}>
      {isLoaded ? (
        <>
          <div
            style={{ height: isMobile ? "350px" : "400px", overflow:"hidden", ...sx }}
            ref={mapRef}
          />
          {/* Centered marker overlay */}
          <div
            style={{
              position: "absolute",
              top: "55%",
              left: "50%",
              transform: "translate(-50%, -100%)", // marker tip at center
              pointerEvents: "none",
              zIndex: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}>
            {/* Simple SVG marker icon */}
            <Typography
              align="center"
              bgcolor={"#fff"}
              p={1}
              fontSize={12}
              borderRadius={4}>
              Drag Map to Move Pin
            </Typography>
            <Image src={IMAGES.mapPin} alt="Map Pin" width={65} height={60} />
          </div>
        </>
      ) : (
        <div
          style={{
            maxWidth: "100%",
            height: isMobile ? "350px" : "400px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f5f5f5",
            ...sx,
          }}>
          <p>Maps loading...</p>
        </div>
      )}
    </div>
  );
};

export default MapsComponent;
